/**
 * Ein Hinweis, der stehen bleibt — sichtbar, aber nicht im Weg.
 *
 * Er beantwortet eine andere Frage als die Nachbarn im Designsystem, und die
 * Abgrenzung ist der Grund, warum es ihn gibt:
 *
 *     Fehlermeldung  etwas ist **schiefgegangen**, mit Fehlercode und
 *                    Correlation-ID aus dem Vertrag in `04` §4.6
 *     Zustand        wie etwas **dasteht** — erfolgreich, in Arbeit, fehlerhaft
 *     Ladezustand    ob gerade **etwas passiert** und was gilt
 *     Hinweis        etwas **fehlt oder ist eingeschränkt**, ohne dass ein
 *                    Fehler vorliegt. Die Oberfläche läuft weiter
 *
 * Zwei Stellen im Portal brauchten das vor diesem Bauteil und lösten es
 * jeweils selbst: der Konfigurationshinweis der Shell (ADR-044, Auflage 3) und
 * die Rahmenansicht, wenn ein eingebetteter Dienst sich nicht meldet. Die
 * zweite hatte die Gestalt, die erste borgte sich deren CSS-Klasse —
 * `rahmenansicht__hinweis` stand auf einer Seite, die keinen Rahmen zeigt.
 *
 * Das ist die Sorte Kopplung, gegen die ein Designsystem gebaut wird: Wer die
 * Rahmenansicht umgestaltet, verändert sonst ungewollt eine Meldung, die mit
 * ihr nichts zu tun hat — und merkt es nicht, weil beide Stellen nie
 * gleichzeitig auf dem Bildschirm sind.
 *
 * **Die Gestalt ist unverändert übernommen**, nicht neu entworfen: Kante links
 * statt Rahmen ringsum, damit der Hinweis auffällt, ohne wie ein eigenes
 * Fenster zu wirken.
 */

import type { ReactNode } from "react";

export const HINWEISARTEN = ["warnung", "neutral"] as const;
export type Hinweisart = (typeof HINWEISARTEN)[number];

export interface HinweisEigenschaften {
  /**
   * Worum es geht — der fett gesetzte Anfang.
   *
   * **Pflicht**, und zwar aus demselben Grund wie beim `Zustand`: Ein Hinweis,
   * der nur aus einer farbigen Kante und Fließtext besteht, unterscheidet sich
   * für jemanden mit Farbsehschwäche nicht vom Absatz darüber. `07` §7.14
   * schließt farbabhängige Statusinformation aus.
   */
  readonly titel: string;
  /**
   * Der Fließtext. Als JSX-Kind wie beim `Knopf` und nicht als benannte Prop
   * wie beim `Zustand`: Hier steht ein Satz mit Verweisen darin, kein Wert.
   */
  readonly children: ReactNode;
  /** `warnung` ist die Vorgabe: Ein Hinweis ohne Anlass gehört nicht dorthin. */
  readonly art?: Hinweisart;
}

export function Hinweis({ titel, children, art = "warnung" }: HinweisEigenschaften) {
  return (
    // `role="status"`: Der Hinweis erscheint oft **nach** dem ersten Rendern —
    // wenn die Konfiguration geladen ist, wenn eine Frist abgelaufen ist. Ohne
    // die Rolle bliebe er für Vorlesewerkzeuge unbemerkt, weil niemand mehr
    // hinsieht. `alert` wäre falsch: Es unterbricht, und hier ist nichts
    // kaputt.
    <p className={`hinweis hinweis--${art}`} role="status">
      <strong>{titel}</strong> {children}
    </p>
  );
}
